import { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import NotificationsDropdown from './NotificationsDropdown';
import {
  LayoutDashboard, Package, ChefHat, BarChart3, Trash2, Warehouse,
  FileText, Bell, CreditCard, Settings, LogOut, Menu, X, Crown, Users, Download, Smartphone, ClipboardList, AlertTriangle, Lock
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/ingredients', icon: Package, label: 'Ingredientes' },
  { to: '/recipes', icon: ChefHat, label: 'Recetas' },
  { to: '/menu-engineering', icon: BarChart3, label: 'Menu Engineering' },
  { to: '/waste', icon: Trash2, label: 'Mermas' },
  { to: '/stock', icon: Warehouse, label: 'Stock' },
  { to: '/reports', icon: FileText, label: 'Reportes' },
  { to: '/alerts', icon: Bell, label: 'Alertas' },
  { to: '/import-export', icon: Download, label: 'Importar/Exportar' },
  { to: '/audit-log', icon: ClipboardList, label: 'Auditoria' },
  { to: '/subscription', icon: CreditCard, label: 'Suscripcion' },
  { to: '/team', icon: Users, label: 'Mi Cuenta' },
  { to: '/settings', icon: Settings, label: 'Ajustes' },
];

export default function DashboardLayout({ children }) {
  const { user, logout, permissions } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setCanInstall(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const hasSub = user?.has_subscription || user?.subscription_status === 'active';
  const trial = user?.trial || {};
  const trialExpired = trial.trial_expired && !hasSub && user?.role !== 'admin';

  return (
    <div className="flex h-[100dvh] bg-[#050505]" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 border-r border-white/10 bg-[#050505] transform transition-transform duration-300 lg:relative lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="flex flex-col h-full">
          <div className="px-6 py-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <img src="/m3-logo.png" alt="M3" className="h-10 w-auto" data-testid="logo" />
              <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/50 hover:text-white" data-testid="close-sidebar-btn">
                <X size={20} />
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Gastronomy Intelligence</p>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {navItems
              .filter(({ to }) => {
                if (to === '/audit-log' || to === '/team') return permissions.can_manage_users;
                if (to === '/reports') return permissions.can_export;
                return true;
              })
              .map(({ to, icon: Icon, label }) => (
              <NavLink
                key={to}
                to={to}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${to.slice(1)}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-all duration-200 ${
                    isActive
                      ? 'border-l-2 border-gold bg-white/5 text-gold'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <Icon size={16} strokeWidth={1.5} />
                <span className="font-body font-medium">{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="px-4 py-4 border-t border-white/10">
            {!hasSub && (
              <div className="mb-3 px-3 py-2 bg-gold/10 border border-gold/30" data-testid="trial-badge">
                <div className="flex items-center gap-2 text-gold text-xs">
                  <Crown size={14} />
                  <span className="uppercase tracking-widest font-semibold">Modo Prueba</span>
                </div>
              </div>
            )}
            
            {/* Install App Option - Only show if can install and not already standalone */}
            {canInstall && !isStandalone && (
              <button 
                onClick={handleInstall}
                data-testid="install-app-sidebar"
                className="mb-3 w-full flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-gold/20 to-gold/10 border border-gold/30 text-gold text-xs transition-all hover:from-gold/30 hover:to-gold/20"
              >
                <Download size={14} />
                <span className="font-semibold uppercase tracking-wide">Instalar App</span>
              </button>
            )}
            
            <div className="flex items-center gap-3 px-3 py-2">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 bg-gold/20 flex items-center justify-center text-gold text-sm font-bold">
                  {user?.name?.[0]?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-white truncate">{user?.name}</p>
                <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
              </div>
            </div>
            <button onClick={handleLogout} data-testid="logout-btn" className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-xs text-white/40 hover:text-red-400 transition-colors">
              <LogOut size={14} />
              <span>Cerrar sesion</span>
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-0 min-w-0 max-w-full">
        <header className="sticky top-0 z-30 h-14 flex items-center justify-between px-6 border-b border-white/10 bg-[#050505]/80 backdrop-blur-xl" data-testid="top-header">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden text-white/60 hover:text-white" data-testid="open-sidebar-btn">
            <Menu size={20} />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <NotificationsDropdown />
            <span className="text-xs text-white/40 uppercase tracking-widest">{user?.currency || 'USD'}</span>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 lg:p-8">
          {/* Trial expired overlay */}
          {trialExpired && (
            <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" data-testid="trial-expired-overlay">
              <div className="bg-[#0A0A0A] border border-red-500/30 max-w-md w-full p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center">
                  <Lock size={28} className="text-red-400" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-white mb-3">Periodo de Prueba Finalizado</h2>
                <p className="text-white/60 text-sm mb-6">
                  Tu periodo de prueba gratuita de 7 dias ha expirado. Para continuar usando todas las funciones de M3, activa tu suscripcion.
                </p>
                <div className="bg-red-500/5 border border-red-500/20 p-4 mb-6">
                  <div className="flex items-center justify-center gap-2 text-red-400 mb-2">
                    <AlertTriangle size={16} />
                    <span className="text-xs uppercase tracking-widest font-bold">Acceso Bloqueado</span>
                  </div>
                  <p className="text-xs text-white/40">Tus datos estan seguros. Al suscribirte recuperaras acceso completo.</p>
                </div>
                <button
                  onClick={() => navigate('/subscription')}
                  data-testid="trial-subscribe-btn"
                  className="w-full py-4 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors mb-3"
                >
                  <CreditCard size={14} className="inline mr-2" />
                  Suscribirme Ahora
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full py-3 border border-white/10 text-white/40 text-xs hover:text-white hover:border-white/30 transition-colors"
                >
                  Cerrar Sesion
                </button>
              </div>
            </div>
          )}

          {/* Trial warning banner (shows during trial, not expired) */}
          {!hasSub && !trialExpired && trial.days_remaining !== undefined && trial.days_remaining <= 7 && user?.role !== 'admin' && (
            <div className={`mb-4 p-4 border flex items-center gap-3 ${trial.days_remaining <= 2 ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20'}`} data-testid="trial-warning-banner">
              <AlertTriangle size={18} className={trial.days_remaining <= 2 ? 'text-red-400' : 'text-amber-400'} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${trial.days_remaining <= 2 ? 'text-red-400' : 'text-amber-400'}`}>
                  {trial.days_remaining === 0 ? 'Ultimo dia de prueba!' : `Te quedan ${trial.days_remaining} dia${trial.days_remaining !== 1 ? 's' : ''} de prueba`}
                </p>
                <p className="text-xs text-white/40">Suscribete para mantener acceso completo a M3</p>
              </div>
              <button
                onClick={() => navigate('/subscription')}
                data-testid="trial-banner-subscribe-btn"
                className="px-4 py-2 bg-gold text-black text-xs font-bold uppercase tracking-widest hover:bg-gold/90 transition-colors flex-shrink-0"
              >
                Suscribirme
              </button>
            </div>
          )}
          {children}
        </div>
      </main>
    </div>
  );
}
